import { Injectable } from '@angular/core';
import {
  catchError,
  combineLatest,
  EMPTY,
  map,
  mergeMap,
  Observable,
  of,
  switchMap,
  take,
} from 'rxjs';
import cloneDeep from 'lodash.clonedeep';
import { v4 as uuid } from 'uuid';

import {
  DprActivitiesGridSync,
  DprActivitiesGridSyncResult,
  DprHseGridSync,
  DprPersonnelGridSync,
  DprWeatherGridSync,
  DprConsumableGridSync,
  DprSummaryGridSync,
  DprSummaryGridSyncResult,
  DprProgressGridSync,
  DprAppendixGridSync,
  DprAppendixGridSyncResult,
  DprHiddenGridSync,
  DprStatusTrackerGridSync,
  DprProgressWithoutTargetGridSync,
  DprFreeTextFieldSync,
  DprFreeTextFieldSyncResult,
  DprForecastActivitiesGridSync,
  DprForecastActivitiesGridSyncResult,
  DprHiddenGridSyncResult,
  DprTableOfColumnGridSync,
} from '@dpr-details/models/sync';
import {
  DprDetailsSync,
  DprHse,
  DprPersonnel,
  DprConsumable,
  DprWeather,
  DprProgress,
  DprDetails,
  DprStatusTracker,
  DprTableOfColumns,
  DprProgressWithoutTarget,
} from '@shared/models/dpr';
import { OfflineFacade } from '@shared/state/facades';
import {
  DprDetailsSyncModelBuilderService,
  DprDetailsOfflineSaveService,
} from '.';
import {
  CopyPreviousDprForecastActivitiesOptions,
  CopyPreviousDprForecastActivitiesResult,
} from '@dpr-details/models';
import { DprForecastActivityGridData } from '@dpr-details/models/grid';
import { findPreviousDprDetails } from '@dpr-details/utils';
import { DprDetailsWarningService } from '@shared/services/dpr';
import { SyncService } from '@shared/services/grid';

@Injectable()
export class DprDetailsOfflineService {
  constructor(
    private offlineFacade: OfflineFacade,
    private dprDetailsSyncModelBuilderService: DprDetailsSyncModelBuilderService,
    private dprDetailsOfflineSaveService: DprDetailsOfflineSaveService,
    private dprDetailsWarningService: DprDetailsWarningService,
    private syncService: SyncService
  ) {}

  saveDprActivities(
    model: DprActivitiesGridSync
  ): Observable<DprActivitiesGridSyncResult> {
    return this.getOrCreateChanges(model.dprDetailsId).pipe(
      take(1),
      mergeMap((dprDetailsChanges) => {
        dprDetailsChanges.dprActivities = model.syncDPRActivities;
        dprDetailsChanges.dprParallelActivities =
          model.syncDPRParallelActivities;

        this.offlineFacade.updateDprDetailsOfflineChanges(dprDetailsChanges);

        return combineLatest([
          this.dprDetailsOfflineSaveService.applyDprActivityOfflineChanges(
            model.syncDPRActivities,
            model.dprDetailsId
          ),
          this.dprDetailsOfflineSaveService.applyDprParallelActivityOfflineChanges(
            model.syncDPRParallelActivities,
            model.dprDetailsId
          ),
          this.offlineFacade.getDprDetails(model.dprDetailsId).pipe(take(1)),
        ]);
      }),
      mergeMap(
        ([
          dprActivitiesSyncResult,
          dprParallelActivitiesSyncResult,
          dprDetails,
        ]) => {
          const updatedDprDetails = {
            ...cloneDeep(dprDetails),
            dprActivities: dprActivitiesSyncResult.dprActivities,
            dprParallelActivities:
              dprParallelActivitiesSyncResult.dprParallelActivities,
          } as DprDetails;

          this.dprDetailsWarningService.updateDprWarningStatus(
            updatedDprDetails
          );

          return of({
            dprActivities: dprActivitiesSyncResult.dprActivities,
            dprParallelActivities:
              dprParallelActivitiesSyncResult.dprParallelActivities,
            firstWarning: updatedDprDetails.firstWarning,
            warningStatusTab: updatedDprDetails.warningStatusTab,
          });
        }
      ),
      catchError((error) => {
        console.error('Error saving DPR Activities', error);
        return EMPTY;
      })
    );
  }

  saveDprStatusTracker(
    model: DprStatusTrackerGridSync
  ): Observable<DprStatusTracker> {
    return this.getOrCreateChanges(model.dprStatusTracker.dprDetailId).pipe(
      take(1),
      mergeMap((dprDetailsChanges) => {
        dprDetailsChanges.dprStatusTracker = model.dprStatusTracker;

        this.offlineFacade.updateDprDetailsOfflineChanges(dprDetailsChanges);

        return this.dprDetailsOfflineSaveService.applyDprStatusTrackerOfflineChanges(
          model
        );
      }),
      catchError((error) => {
        console.error('Error saving DPR Status Tracker', error);
        return EMPTY;
      })
    );
  }

  saveDprWeather(model: DprWeatherGridSync): Observable<DprWeather> {
    return this.getOrCreateChanges(model.dprWeather.dprDetailId).pipe(
      take(1),
      mergeMap((dprDetailsChanges) => {
        dprDetailsChanges.dprWeather = model.dprWeather;

        this.offlineFacade.updateDprDetailsOfflineChanges(dprDetailsChanges);

        return this.dprDetailsOfflineSaveService.applyDprWeatherOfflineChanges(
          model
        );
      }),
      catchError((error) => {
        console.error('Error saving DPR Weather', error);
        return EMPTY;
      })
    );
  }
  saveDprHse(model: DprHseGridSync): Observable<DprHse> {
    return this.getOrCreateChanges(model.dprHse.dprDetailId).pipe(
      take(1),
      mergeMap((dprDetailsChanges) => {
        dprDetailsChanges.dprHSE = model.dprHse;

        this.offlineFacade.updateDprDetailsOfflineChanges(dprDetailsChanges);

        return this.dprDetailsOfflineSaveService.applyDprHseOfflineChanges(
          model
        );
      }),
      catchError((error) => {
        console.error('Error saving DPR HSE', error);
        return EMPTY;
      })
    );
  }

  saveDprPersonnel(model: DprPersonnelGridSync): Observable<DprPersonnel> {
    return this.getOrCreateChanges(model.dprPersonnel.dprDetailId).pipe(
      take(1),
      mergeMap((dprDetailsChanges) => {
        dprDetailsChanges.dprPersonnel = model.dprPersonnel;

        this.offlineFacade.updateDprDetailsOfflineChanges(dprDetailsChanges);

        return this.dprDetailsOfflineSaveService.applyDprPersonnelOfflineChanges(
          model
        );
      }),
      catchError((error) => {
        console.error('Error saving DPR Personnel', error);
        return EMPTY;
      })
    );
  }

  saveDprConsumable(model: DprConsumableGridSync): Observable<DprConsumable> {
    return this.getOrCreateChanges(model.dprConsumable.dprDetailId).pipe(
      take(1),
      mergeMap((dprDetailsChanges) => {
        dprDetailsChanges.dprConsumable = model.dprConsumable;

        this.offlineFacade.updateDprDetailsOfflineChanges(dprDetailsChanges);

        return this.dprDetailsOfflineSaveService.applyDprConsumableOfflineChanges(
          model
        );
      }),
      catchError((error) => {
        console.error('Error saving DPR Consumable', error);
        return EMPTY;
      })
    );
  }

  saveDprSummary(
    model: DprSummaryGridSync
  ): Observable<DprSummaryGridSyncResult> {
    return this.getOrCreateChanges(model.dprSummary.dprDetailId).pipe(
      take(1),
      mergeMap((dprDetailsChanges) => {
        dprDetailsChanges.dprSummary = model.dprSummary;

        this.offlineFacade.updateDprDetailsOfflineChanges(dprDetailsChanges);

        return this.dprDetailsOfflineSaveService.applyDprSummaryOfflineChanges(
          model
        );
      }),
      catchError((error) => {
        console.error('Error saving DPR Summary', error);
        return EMPTY;
      })
    );
  }

  saveDprFreeTextFields(
    model: DprFreeTextFieldSync
  ): Observable<DprFreeTextFieldSyncResult> {
    return this.getOrCreateChanges(model.dprFreeTextField.dprDetailId).pipe(
      take(1),
      mergeMap((dprDetailsChanges) => {
        const existingFtf = (dprDetailsChanges.dprFreeTextFields ?? []).find(
          (x) => x.freeTextFieldId === model.dprFreeTextField.freeTextFieldId
        );
        if (existingFtf) {
          dprDetailsChanges.dprFreeTextFields =
            dprDetailsChanges.dprFreeTextFields.map((x) =>
              x.freeTextFieldId === model.dprFreeTextField.freeTextFieldId
                ? model.dprFreeTextField
                : x
            );
        } else {
          dprDetailsChanges.dprFreeTextFields = [
            ...(dprDetailsChanges.dprFreeTextFields ?? []),
            model.dprFreeTextField,
          ];
        }

        this.offlineFacade.updateDprDetailsOfflineChanges(dprDetailsChanges);

        return this.dprDetailsOfflineSaveService.applyDprFreeTextFieldOfflineChanges(
          model
        );
      }),
      catchError((error) => {
        console.error('Error saving DPR Free Text Fields', error);
        return EMPTY;
      })
    );
  }

  syncDprProgress(model: DprProgressGridSync): Observable<DprProgress> {
    return this.getOrCreateChanges(model.dprprogress.dprDetailId).pipe(
      take(1),
      mergeMap((dprDetailsChanges) => {
        dprDetailsChanges.dprProgress = model.dprprogress;

        this.offlineFacade.updateDprDetailsOfflineChanges(dprDetailsChanges);

        return this.dprDetailsOfflineSaveService.applyDprProgressOfflineChanges(
          model
        );
      }),
      catchError((error) => {
        console.error('Error saving DPR Progress', error);
        return EMPTY;
      })
    );
  }

  syncDprProgressWithoutTarget(
    model: DprProgressWithoutTargetGridSync
  ): Observable<DprProgressWithoutTarget> {
    return this.getOrCreateChanges(
      model.dprProgressWithoutTarget.dprDetailId
    ).pipe(
      take(1),
      mergeMap((dprDetailsChanges) => {
        dprDetailsChanges.dprProgressWithoutTarget =
          model.dprProgressWithoutTarget;

        this.offlineFacade.updateDprDetailsOfflineChanges(dprDetailsChanges);

        return this.dprDetailsOfflineSaveService.applyDprProgessWithoutTargetOfflineChanges(
          model
        );
      }),
      catchError((error) => {
        console.error('Error saving DPR Progress Without Target', error);
        return EMPTY;
      })
    );
  }

  saveDprForecastActivities(
    model: DprForecastActivitiesGridSync
  ): Observable<DprForecastActivitiesGridSyncResult> {
    return this.getOrCreateChanges(model.dprDetailsId).pipe(
      take(1),
      mergeMap((dprDetailsChanges) => {
        dprDetailsChanges.dprForecastActivities =
          model.syncDprForecastActivities;
        this.offlineFacade.updateDprDetailsOfflineChanges(dprDetailsChanges);

        return this.dprDetailsOfflineSaveService.applyDprForecastActivityOfflineChanges(
          model
        );
      }),
      catchError((error) => {
        console.error('Error saving DPR Forecast Activities', error);
        return EMPTY;
      })
    );
  }

  copyDprForecastActivitiesFromPreviousDpr(
    options: CopyPreviousDprForecastActivitiesOptions
  ): Observable<CopyPreviousDprForecastActivitiesResult> {
    return this.offlineFacade.getDprDetails(options.dprDetailsId).pipe(
      take(1),
      switchMap((curDprDetails) =>
        this.getPreviousDprDetails(curDprDetails.dprDateTime).pipe(
          switchMap((prevDprDetails) => {
            if (!prevDprDetails || !prevDprDetails.dprForecastActivities) {
              return of({
                dprForecastActivities: curDprDetails.dprForecastActivities,
                firstWarning: curDprDetails.firstWarning,
                warningStatusTab: curDprDetails.warningStatusTab,
                dprDetailId: curDprDetails.id,
              } as CopyPreviousDprForecastActivitiesResult);
            }

            const addedList = [];

            prevDprDetails.dprForecastActivities.forEach((forecastActivity) => {
              if (
                new Date(forecastActivity.date).getTime() >
                new Date(curDprDetails.dprDateTime).getTime()
              ) {
                const newForecastActivity: DprForecastActivityGridData =
                  cloneDeep(forecastActivity);

                newForecastActivity.id = uuid();
                newForecastActivity.dprDetailId = curDprDetails.id;
                newForecastActivity.syncDprForecastSubActivities =
                  this.syncService.getDefaultSyncModel();
                newForecastActivity.syncDprForecastActivityCustomDataList =
                  this.syncService.getDefaultSyncModel();

                if (
                  forecastActivity.dprForecastSubActivities &&
                  options.shouldCopySubactivities
                ) {
                  newForecastActivity.dprForecastSubActivities.forEach((x) => {
                    x.id = uuid();
                    x.dprForecastActivityId = null;
                  });
                  newForecastActivity.syncDprForecastSubActivities.addedList =
                    newForecastActivity.dprForecastSubActivities;
                }

                if (
                  forecastActivity.dprForecastActivityCustomDataList &&
                  options.shouldCopyCustomData
                ) {
                  newForecastActivity.dprForecastActivityCustomDataList.forEach(
                    (x) => {
                      x.id = uuid();
                      x.dprForecastActivityId = null;
                    }
                  );
                  newForecastActivity.syncDprForecastActivityCustomDataList.addedList =
                    newForecastActivity.dprForecastActivityCustomDataList;
                }

                addedList.push(newForecastActivity);
              }
            });

            return this.getOrCreateChanges(curDprDetails.id).pipe(
              take(1),
              mergeMap((changes) => {
                const changesCopy = cloneDeep(changes);

                if (!changesCopy.dprForecastActivities) {
                  changesCopy.dprForecastActivities =
                    this.syncService.getDefaultSyncModel();
                }
                changesCopy.dprForecastActivities.addedList =
                  changesCopy.dprForecastActivities.addedList.concat(addedList);

                this.offlineFacade.updateDprDetailsOfflineChanges(changesCopy);

                return this.dprDetailsOfflineSaveService
                  .applyDprForecastActivityOfflineChanges({
                    syncDprForecastActivities:
                      changesCopy.dprForecastActivities,
                    dprDetailsId: curDprDetails.id,
                    subprojectId: curDprDetails.dprSubprojectId,
                  })
                  .pipe(
                    map(
                      (x) =>
                        ({
                          dprForecastActivities: x.dprForecastActivities,
                          dprDetailId: x.dprDetailsId,
                          firstWarning: x.firstWarning,
                          warningStatusTab: curDprDetails.warningStatusTab,
                        } as CopyPreviousDprForecastActivitiesResult)
                    )
                  );
              })
            );
          })
        )
      )
    );
  }

  saveDprTableOfColumns(
    model: DprTableOfColumnGridSync
  ): Observable<DprTableOfColumns> {
    return this.getOrCreateChanges(model.dprTableOfColumn.dprDetailId).pipe(
      take(1),
      mergeMap((dprDetailsChanges) => {
        const existingToc = (dprDetailsChanges.dprTableOfColumns ?? []).find(
          (x) => x.tableOfColumnId === model.dprTableOfColumn.tableOfColumnId
        );
        if (existingToc) {
          dprDetailsChanges.dprTableOfColumns =
            dprDetailsChanges.dprTableOfColumns.map((x) =>
              x.tableOfColumnId === model.dprTableOfColumn.tableOfColumnId
                ? model.dprTableOfColumn
                : x
            );
        } else {
          dprDetailsChanges.dprTableOfColumns = [
            ...(dprDetailsChanges.dprTableOfColumns ?? []),
            model.dprTableOfColumn,
          ];
        }

        this.offlineFacade.updateDprDetailsOfflineChanges(dprDetailsChanges);

        return this.dprDetailsOfflineSaveService.applyDprTableOfColumnsOfflineChanges(
          model
        );
      }),
      catchError((error) => {
        console.error('Error saving DPR Table of Columns', error);
        return EMPTY;
      })
    );
  }
  saveDprAppendix(
    model: DprAppendixGridSync
  ): Observable<DprAppendixGridSyncResult> {
    return this.getOrCreateChanges(model.dprDetailId).pipe(
      take(1),
      mergeMap((dprDetailsChanges) => {
        dprDetailsChanges.syncDprAppendixAttachments =
          model.syncDprAppendixAttachments;

        this.offlineFacade.updateDprDetailsOfflineChanges(dprDetailsChanges);

        return this.dprDetailsOfflineSaveService.applyDprAppendixOfflineChanges(
          model
        );
      }),
      catchError((error) => {
        console.error('Error saving DPR Appendix', error);
        return EMPTY;
      })
    );
  }

  saveDprHidden(model: DprHiddenGridSync): Observable<DprHiddenGridSyncResult> {
    return this.getOrCreateChanges(model.dprDetailId).pipe(
      take(1),
      mergeMap((dprDetailsChanges) => {
        dprDetailsChanges.dprHidden = model.syncDprHidden;

        this.offlineFacade.updateDprDetailsOfflineChanges(dprDetailsChanges);

        return this.dprDetailsOfflineSaveService.applyDprHiddenOfflineChanges(
          model
        );
      }),
      catchError((error) => {
        console.error('Error saving DPR Hidden', error);
        return EMPTY;
      })
    );
  }

  getOrCreateChanges(dprDetailId: number | string): Observable<DprDetailsSync> {
    return this.getChangesForDprDetailId(dprDetailId).pipe(
      take(1),
      mergeMap((changes) => {
        if (changes) {
          return of({ ...changes });
        }

        return this.dprDetailsSyncModelBuilderService.forDprDetailId(
          dprDetailId
        );
      })
    );
  }

  getChangesForDprDetailId(
    dprDetailId: number | string
  ): Observable<DprDetailsSync> {
    return this.offlineFacade.query.dprDetailsOfflineChanges$.pipe(
      map((changes) =>
        changes?.find((dprDetails) => dprDetails.id === dprDetailId)
      )
    );
  }

  getPreviousDprDetails(dprDate: Date): Observable<DprDetails> {
    return this.offlineFacade.query.combinedDprDetailsList$.pipe(
      take(1),
      map((dprDetailsList: DprDetails[]) =>
        findPreviousDprDetails(dprDetailsList, dprDate)
      )
    );
  }
}
